import type { ReactNode } from 'react';
import { Card, CardHeader, type CardHeaderProps, LoadError, Skeleton } from '~/components/ui';

/** A persistent load failure; it replaces the body and keeps the header. */
export interface CardError {
  /** What failed, as `LoadError` reads it ("the flag adoption"); defaults to the lower-cased title. */
  what?: string;
  message?: string;
  onRetry?: () => void;
}

interface DashboardCardProps {
  loading?: boolean;
  /** The skeleton shape while loading; a list unless this is a chart. */
  skeleton?: 'chart' | 'list';
  className?: string;
  error?: CardError;
  /** Renders a `CardHeader`; `headerProps` carries its description, icon, actions and the rest. */
  title?: string;
  headerProps?: Omit<CardHeaderProps, 'title'>;
  children?: ReactNode;
}

/** The inside of a card while its query loads — the one look every card shares. */
function CardSkeleton({ variant }: { variant: 'chart' | 'list' }) {
  return (
    <div data-testid="dashboard-card-skeleton" className="p-5 md:p-6">
      <Skeleton
        className={`h-6 bg-gray-200 dark:bg-gray-800 ${variant === 'chart' ? 'w-52' : 'w-40'}`}
      />
      {variant === 'chart' ? (
        <Skeleton variant="block" className="mt-4 h-72" />
      ) : (
        <div className="mt-4 space-y-3">
          <Skeleton variant="row" />
          <Skeleton variant="row" />
          <Skeleton variant="row" />
        </div>
      )}
    </div>
  );
}

/** A dashboard card that shows a skeleton until ITS query settles and a `LoadError` if it failed, so one slow or failed query never blanks or paints another card. */
export function DashboardCard({
  loading = false,
  skeleton = 'list',
  className = '',
  error,
  title,
  headerProps,
  children,
}: DashboardCardProps) {
  return (
    <Card className={`flex flex-col ${className}`}>
      {loading ? (
        <CardSkeleton variant={skeleton} />
      ) : (
        <>
          {(title || headerProps) && <CardHeader title={title} {...headerProps} />}
          <div className="min-h-0 flex-1 p-5 md:p-6">
            {error ? (
              <LoadError what={error.what ?? title?.toLowerCase() ?? 'this card'} {...error} />
            ) : (
              children
            )}
          </div>
        </>
      )}
    </Card>
  );
}
